import React, {
  useState,
  useEffect,
  forwardRef,
  useRef,
  useCallback,
} from "react";
import { InputMask } from "@react-input/mask";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import Flag from "react-world-flags";
import InputAdornment from "@mui/material/InputAdornment";
import useDebounce from "../../hooks/useDebounce";

type InputProps = TextFieldProps & {
  defaultCode: string;
  loading?: boolean;
};

const PhoneNumberInput = forwardRef<HTMLDivElement, InputProps>(
  (
    {
      loading = false,
      disabled = false,
      InputProps,
      defaultCode,
      value,
      onChange,
      ...props
    },
    ref,
  ) => {
    // Internal state for the formatted display value
    const [formattedValue, setFormattedValue] = useState(
      (value as string) || "",
    );

    // Track if we're in domestic mode (UK only, when user types 0)
    const [isDomesticMode, setIsDomesticMode] = useState(false);

    // Track if the component is controlled by user input
    const isInternalUpdateRef = useRef(false);
    const lastPropValueRef = useRef<string>((value as string) || "");

    // Debounce the formatted value to notify parent
    const debouncedFormattedValue = useDebounce({
      value: formattedValue,
      delay: 300,
    });

    const getMask = (code: string, isDomestic: boolean = false) => {
      switch (code) {
        case "US":
          return "___ ___ ____";
        case "UK":
          // UK domestic format: 0NNN NNNN NNNN (11 digits, e.g., 020 7946 0958)
          // UK international format: NNNN NNNNNN (10 digits, e.g., 20 7946 0958)
          return isDomestic ? "_ ___ ____ ____" : "____ ______";
        default:
          return "";
      }
    };

    const getFlagCode = (code: string) => {
      switch (code) {
        case "US":
          return "US";
        case "UK":
          return "GB";
        default:
          return "";
      }
    };

    const flagCode = getFlagCode(defaultCode);

    // Convert domestic format (0NNNN NNNNNN) to international format (+44 NNNN NNNNNN)
    const convertDomesticToInternational = (val: string): string => {
      const digits = val.replace(/\D/g, "");
      // If starts with 0, remove it (UK domestic format)
      if (digits.startsWith("0") && digits.length === 11) {
        return digits.substring(1); // Remove leading 0
      }
      return digits;
    };

    // Convert international format to domestic for display (add 0 if needed)
    const convertInternationalToDomestic = (val: string): string => {
      const digits = val.replace(/\D/g, "");
      // If it's 10 digits and doesn't start with 0, add 0 for domestic display
      if (digits.length === 10 && !digits.startsWith("0")) {
        return `0${digits}`;
      }
      return digits;
    };

    // Format phone number for display
    const applyFormat = (
      val: string,
      code: string,
      isDomestic: boolean = false,
    ) => {
      let formatted = val.replace(/\D/g, "");

      switch (code) {
        case "US":
          // US: Store as 10 digits, display as NNN NNN-NNNN
          if (formatted.length > 6) {
            formatted = formatted.replace(/(\d{3})(\d{3})(\d+)/, "$1 $2-$3");
          } else if (formatted.length > 3) {
            formatted = formatted.replace(/(\d{3})(\d+)/, "$1 $2");
          }
          break;
        case "UK":
          if (isDomestic) {
            // UK Domestic: Display as 0NNN NNNN NNNN (e.g., 020 7946 0958)
            if (formatted.length > 7) {
              // Format: 0NNN NNNN NNNN
              formatted = formatted.replace(
                /(\d)(\d{3})(\d{4})(\d+)/,
                "$1$2 $3 $4",
              );
            } else if (formatted.length > 3) {
              // Partial format: 0NNN NNNN
              formatted = formatted.replace(/(\d)(\d{3})(\d+)/, "$1$2 $3");
            }
          } else {
            // UK International: Display as NNNN NNNNNN (10 digits, e.g., 20 7946 0958)
            if (formatted.length > 4) {
              formatted = formatted.replace(/(\d{4})(\d+)/, "$1 $2");
            }
          }
          break;
        default:
          break;
      }

      return formatted;
    };

    // Get country code prefix for display
    const getCountryCodePrefix = (
      code: string,
      isDomestic: boolean,
    ): string => {
      if (code === "UK" && isDomestic) {
        return "0"; // Domestic format shows 0 instead of +44
      }
      switch (code) {
        case "US":
          return "+1";
        case "UK":
          return "+44";
        default:
          return "";
      }
    };

    // Sync with prop value only when it's an external change (not from our own onChange)
    useEffect(() => {
      const currentPropValue = (value as string) || "";
      const cleanedPropValue = currentPropValue.replace(/\s+/g, "");
      const cleanedLastProp = lastPropValueRef.current.replace(/\s+/g, "");

      // Only sync if:
      // 1. The prop value actually changed externally
      // 2. We're not in the middle of an internal update
      // 3. The cleaned values are different
      if (
        !isInternalUpdateRef.current &&
        cleanedPropValue !== cleanedLastProp &&
        currentPropValue !== lastPropValueRef.current
      ) {
        // For UK: Convert from DB format (10 digits) to domestic display format (0 + 10 digits)
        let displayValue = currentPropValue;
        if (
          defaultCode === "UK" &&
          cleanedPropValue.length === 10 &&
          !cleanedPropValue.startsWith("0")
        ) {
          displayValue = convertInternationalToDomestic(cleanedPropValue);
          setIsDomesticMode(true);
        } else if (defaultCode === "UK" && cleanedPropValue.startsWith("0")) {
          setIsDomesticMode(true);
        } else {
          setIsDomesticMode(false);
        }

        const newFormattedValue = applyFormat(
          displayValue,
          defaultCode,
          isDomesticMode,
        );
        setFormattedValue(newFormattedValue);
        lastPropValueRef.current = currentPropValue;
      }
    }, [value, defaultCode, isDomesticMode]);

    // Notify parent of changes via debounced value
    useEffect(() => {
      let cleanedFormatted = (debouncedFormattedValue as string)
        .replace(/\s+/g, "")
        .replace(/-/g, "");
      const cleanedPropValue = ((value as string) || "")
        .replace(/\s+/g, "")
        .replace(/-/g, "");

      // For UK: Convert domestic format (0NNNN...) to international format (NNNN...) for storage
      if (
        defaultCode === "UK" &&
        cleanedFormatted.startsWith("0") &&
        cleanedFormatted.length === 11
      ) {
        cleanedFormatted = convertDomesticToInternational(cleanedFormatted);
      }

      // For UK: If user clears the field, reset to international mode
      if (defaultCode === "UK" && cleanedFormatted === "") {
        setIsDomesticMode(false);
      }

      // Only call onChange if:
      // 1. The cleaned values are different
      // 2. The formatted value is not empty (or we're clearing it)
      // 3. This is a user-initiated change (not initial load)
      if (cleanedFormatted !== cleanedPropValue) {
        isInternalUpdateRef.current = true;

        if (onChange) {
          onChange({
            target: { value: cleanedFormatted },
          } as React.ChangeEvent<HTMLInputElement>);
        }

        // Reset the flag after parent has time to update
        // This prevents the prop sync effect from interfering
        setTimeout(() => {
          isInternalUpdateRef.current = false;
          lastPropValueRef.current = cleanedFormatted;
        }, 100);
      }
    }, [debouncedFormattedValue, onChange, value, defaultCode]);

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        // Mark as internal update to prevent prop sync
        isInternalUpdateRef.current = true;

        const inputValue = e.target.value;
        const digits = inputValue.replace(/\D/g, "");

        // For UK: Detect if user is typing in domestic format (starts with 0)
        if (defaultCode === "UK") {
          if (digits.startsWith("0")) {
            setIsDomesticMode(true);
          } else if (digits === "") {
            // If user clears the field, reset to international mode
            setIsDomesticMode(false);
          }
        }

        // Update local state immediately for responsive UI
        setFormattedValue(inputValue);
      },
      [defaultCode],
    );

    return (
      <TextField
        ref={ref}
        fullWidth
        autoComplete="off"
        value={formattedValue}
        onChange={handleInputChange}
        disabled={disabled || loading}
        InputProps={{
          ...InputProps,
          startAdornment: (
            <InputAdornment position="start" className="pr-3">
              <Flag code={flagCode} style={{ width: 24, height: 16 }} />
              <span className="pl-2">
                {getCountryCodePrefix(defaultCode, isDomesticMode)}
              </span>
            </InputAdornment>
          ),
          readOnly: InputProps?.readOnly || loading,
          inputComponent: InputMask,
          inputProps: {
            mask: getMask(defaultCode, isDomesticMode),
            replacement: { _: /\d/ },
          },
        }}
        {...props}
      />
    );
  },
);

PhoneNumberInput.displayName = "PhoneNumberInput";

export default PhoneNumberInput;
